import { action, flow, makeObservable, observable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore } from '@roc/feature-app-core';
import { PostClosingService } from '../services/postClosingService';
import { PostClosingTabsEnum } from '../constants/tabs';

export abstract class PostClosingBaseStore {
  globalStore: GlobalStore;
  service: PostClosingService;
  tab: PostClosingTabsEnum;
  postClosingGridStore: GridStore;
  selectedLoanIds: number[] = [];

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new PostClosingService();
    this.selectedLoanIds = [];
    this.tab = PostClosingTabsEnum.TRAILING_DOCUMENTS;

    this.postClosingGridStore = new GridStore(
      () => this.fetchPostClosingGrid(),
      null,
      50
    );

    makeObservable(this, {
      tab: observable,
      selectedLoanIds: observable,
      setTab: action,
      reset: action,
      resetAndFetchGridData: flow,
      setSelectedLoanId: action,
      clearSelectedLoanIds: action,
      fetchOnlyGridData: flow,
    });
  }

  private async fetchPostClosingGrid(): Promise<ApiResponse> {
    try {
      const meta = this.postClosingGridStore.gridData.meta;
      const filters = meta.filters || {};
      const dropdownFilters = meta.dropdownFilters || {};

      const pageNumber = meta.pageNumber;
      const pageSize = meta.pageSize;
      const sortBy = meta.sortBy;
      const sortDir = meta.sortDir;

      let loanProcess = '';
      switch (this.tab) {
        case PostClosingTabsEnum.TRAILING_DOCUMENTS:
          loanProcess = 'Post Closing Trailing Documents';
          break;
        case PostClosingTabsEnum.COLLATERAL_MANAGEMENT:
          loanProcess = 'Post Closing Collateral Management';
          break;
        case PostClosingTabsEnum.SPECIALIST_REASSIGNMENT:
          loanProcess = 'Post Closing Reassign Specialist';
          break;
      }

      const urlParams = new URLSearchParams({
        page: String(pageNumber),
        pageSize: String(pageSize),
        sortBy,
        sortDir,
        filters: JSON.stringify(filters),
        dropdownFilters: JSON.stringify(dropdownFilters),
      }).toString();

      if (this.tab === PostClosingTabsEnum.SPECIALIST_REASSIGNMENT) {
        return await this.service.getPostClosingSpecialistReassignGrid(
          urlParams,
          loanProcess
        );
      }

      if (this.tab === PostClosingTabsEnum.COLLATERAL_MANAGEMENT) {
        return await this.service.getPostClosingCollateralGrid(
          urlParams,
          loanProcess
        );
      }

      return await this.service.getPostClosingTrailingGrid(
        urlParams,
        loanProcess
      );
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error fetching Post Closing data.',
      });
      return {
        data: {
          rows: [],
          meta: {
            total: 0,
            pageNumber: 1,
          },
        },
        headers: {},
      };
    }
  }

  setTab(tab: PostClosingTabsEnum) {
    this.tab = tab;
  }

  *resetAndFetchGridData() {
    this.clearSelectedLoanIds();
    this.postClosingGridStore.reset();
    yield this.postClosingGridStore.fetchGridData();
  }

  setSelectedLoanId(id: number, selected: boolean) {
    if (selected && !this.selectedLoanIds.includes(id)) {
      this.selectedLoanIds.push(id);
    } else if (!selected) {
      this.selectedLoanIds = this.selectedLoanIds.filter(i => i !== id);
    }
  }

  clearSelectedLoanIds() {
    this.selectedLoanIds = [];
  }

  *fetchOnlyGridData() {
    yield this.postClosingGridStore.fetchGridData();
  }

  abstract reset(): void;
  abstract fetchDropdowns(): any;
  
}
